import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const navItems = [
  { name: 'Inventory', path: '/inventory' },
  { name: 'Financing', path: '/financing' },
  { name: 'Service', path: '/service' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

const services = [
  {
    icon: 'ri-oil-line',
    title: 'Oil Change & Fluids',
    description:
      'Premium synthetic oil changes and complete fluid services to keep your engine running at peak performance.',
    price: 'From $89',
  },
  {
    icon: 'ri-brake-line',
    title: 'Brake Service',
    description:
      'Expert brake inspection, pad replacement, and rotor resurfacing for optimal stopping power.',
    price: 'From $149',
  },
  {
    icon: 'ri-settings-3-line',
    title: 'Engine Diagnostics',
    description:
      'Advanced computer diagnostics to identify and resolve any engine performance issues.',
    price: 'From $99',
  },
  {
    icon: 'ri-car-washing-line',
    title: 'Luxury Detailing',
    description:
      "Professional interior and exterior concierge detailing to restore your vehicle's showroom finish.",
    price: 'From $199',
  },
  {
    icon: 'ri-steering-2-line',
    title: 'Wheel Alignment',
    description:
      'Precision alignment services to ensure even tire wear and optimal handling characteristics.',
    price: 'From $129',
  },
  {
    icon: 'ri-battery-2-charge-line',
    title: 'Battery Service',
    description:
      'Battery testing, replacement, and electrical system diagnostics for reliable starts every time.',
    price: 'From $49',
  },
  {
    icon: 'ri-temp-cold-line',
    title: 'A/C Service',
    description:
      'Complete air conditioning inspection, recharge, and repair services for year-round comfort.',
    price: 'From $119',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Full Inspection',
    description:
      'Comprehensive multi-point inspection covering all major vehicle systems and safety components.',
    price: 'From $79',
  },
];

export default function ServicePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    serviceType: '',
    preferredDate: '',
    preferredTime: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  const servicesRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);

  /* --------------------------------------------------------------------- */
  /* Scroll handling – toggles a subtle navbar background on scroll */
  /* --------------------------------------------------------------------- */
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Schema.org JSON-LD injection
  useEffect(() => {
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://paramountautosusa.com';
    
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AutoRepair",
          "@id": `${baseUrl}/service#autorepair`,
          "name": "Paramount Motors Service Center",
          "url": `${baseUrl}/service`,
          "description": "Expert automotive service and maintenance for luxury and exotic vehicles. State-of-the-art service center with certified technicians.",
          "image": "https://public.readdy.ai/ai/img_res/d8fa9a5cfc43054f25c58c48f578d4ec.jpg",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "1250 Luxury Auto Drive",
            "addressLocality": "Beverly Hills",
            "addressRegion": "CA",
            "postalCode": "90210",
            "addressCountry": "US"
          },
          "telephone": "+1-310-555-0199",
          "email": "service@paramountmotors.com",
          "priceRange": "$$-$$$",
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "07:30",
              "closes": "18:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Saturday",
              "opens": "08:00",
              "closes": "16:00"
            }
          ],
          "areaServed": {
            "@type": "GeoCircle",
            "geoMidpoint": {
              "@type": "GeoCoordinates",
              "latitude": "34.0736",
              "longitude": "-118.4004"
            },
            "geoRadius": "50000"
          }
        },
        {
          "@type": "LocalBusiness",
          "@id": `${baseUrl}/#organization`,
          "name": "Paramount Motors",
          "url": baseUrl,
          "logo": "https://static.readdy.ai/image/41574e0492e980fd88e2c6f8d59234d9/ff8f99c2c3d017ecd9846c9b47aca342.png"
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  /* --------------------------------------------------------------------- */
  /* IntersectionObserver – reveals sections when they enter the viewport */
  /* --------------------------------------------------------------------- */
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.target.id) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    [servicesRef, formRef, locationRef].forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  /* --------------------------------------------------------------------- */
  /* Form handling utilities */
  /* --------------------------------------------------------------------- */
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Encode form data safely – fallback to empty string for unexpected values
    const body = new URLSearchParams(
      Object.entries(formData).reduce((acc, [key, val]) => {
        acc[key] = typeof val === 'string' ? val : '';
        return acc;
      }, {} as Record<string, string>)
    ).toString();

    try {
      await fetch('https://readdy.ai/api/form/d6796ilbiueuoqm1r0s0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        vehicleMake: '',
        vehicleModel: '',
        vehicleYear: '',
        serviceType: '',
        preferredDate: '',
        preferredTime: '',
        message: '',
      });
    } catch (err) {
      console.error('Form submission failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* --------------------------------------------------------------------- */
  /* Reusable Tailwind classes for inputs – mobile‑first, compact padding   */
  /* --------------------------------------------------------------------- */
  const inputClass =
    'w-full px-3 py-2.5 bg-[#1a1a1a] border border-white/10 focus:border-[#C9A84C] rounded-sm text-sm text-white placeholder-white/20 focus:outline-none transition-all';

  return (
    <div className="min-h-screen bg-[#0d0d0d]">
      <title>Auto Service Center | Paramount Autos USA – Miami, FL</title>
      <meta name="description" content="Expert automotive service and maintenance at Paramount Autos USA in Miami, FL. Oil changes, brakes, diagnostics, luxury detailing and more. Schedule your appointment online." />
      <link rel="canonical" href="https://paramountautosusa.com/service" />
      {/* --------------------------- Navbar --------------------------- */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-[#0d0d0d]/95 backdrop-blur-md shadow-lg shadow-black/30 border-b border-[#C9A84C]/20 py-3 sm:py-4'
            : 'bg-transparent py-4 sm:py-6'
        }`}
      >
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2 sm:gap-3 cursor-pointer"
            >
              <i className="ri-shield-star-fill text-[#C9A84C] text-lg sm:text-xl"></i>
              <span
                className="text-base sm:text-lg font-bold tracking-widest text-white uppercase truncate max-w-[140px] xs:max-w-[180px] sm:max-w-none"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  letterSpacing: '0.15em',
                }}
              >
                Paramount Motors
              </span>
            </Link>

            {/* Desktop navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map(item => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`text-xs font-medium tracking-widest uppercase transition-all cursor-pointer relative ${
                    item.name === 'Service'
                      ? 'text-[#C9A84C]'
                      : 'text-white/60 hover:text-[#C9A84C]'
                  }`}
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {item.name}
                  {item.name === 'Service' && (
                    <span className="absolute -bottom-1 left-0 w-full h-px bg-[#C9A84C]"></span>
                  )}
                </Link>
              ))}
            </div>

            {/* Call‑to‑action button (desktop) */}
            <button
              className="hidden lg:block px-6 py-2.5 rounded-sm text-xs font-bold tracking-widest uppercase bg-[#C9A84C] text-[#0d0d0d] hover:bg-[#e0bc5e] transition-all whitespace-nowrap"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Schedule Test Drive
            </button>

            {/* Hamburger – mobile */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center cursor-pointer text-white"
            >
              <i
                className={`${
                  isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
                } text-xl sm:text-2xl`}
              ></i>
            </button>
          </div>

          {/* Mobile menu – collapses under hamburger */}
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4">
              <div className="flex flex-col gap-4">
                {navItems.map(item => (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-xs font-medium tracking-widest uppercase text-white/60 hover:text-[#C9A84C] transition-all"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {item.name}
                  </Link>
                ))}
                <button
                  className="mt-2 px-6 py-3 rounded-sm text-xs font-bold tracking-widest uppercase bg-[#C9A84C] text-[#0d0d0d] whitespace-nowrap text-center"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Schedule Test Drive
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* --------------------------- Hero --------------------------- */}
      <section className="relative h-72 sm:h-96 lg:h-[500px] flex items-end justify-start overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20luxury%20car%20service%20center%20dark%20moody%20interior%20dramatic%20lighting%20premium%20sports%20cars%20on%20hydraulic%20lifts%20certified%20mechanics%20dark%20uniforms%20precision%20tools%20polished%20dark%20floors%20cinematic%20industrial%20design%20gold%20accent%20lighting&width=1920&height=600&seq=pm_service_hero&orientation=landscape"
            alt="Paramount Motors Service Center"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/90 via-[#0d0d0d]/60 to-[#0d0d0d]/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-black/40"></div>
        </div>

        <div className="relative z-10 px-4 sm:px-8 lg:px-16 pb-8 sm:pb-12 lg:pb-16 max-w-3xl">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="h-px w-6 sm:w-8 bg-[#C9A84C]"></div>
            <span
              className="text-[#C9A84C] text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Service Center
            </span>
          </div>

          <h1
            className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white mb-3 sm:mb-4 leading-tight"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Expert Care<br />
            <span className="text-[#C9A84C]">For Your Vehicle</span>
          </h1>

          <p
            className="text-white/70 max-w-2xl mx-auto text-sm leading-relaxed"
            style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
          >
            Keep your vehicle performing at its peak with our state-of-the-art service center. From routine maintenance to performance upgrades, our certified technicians treat every vehicle with the care it deserves.
          </p>
        </div>
      </section>

      {/* --------------------------- Services Grid --------------------------- */}
      <section
        id="services-section"
        ref={servicesRef}
        className={`py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-16 bg-[#111111] transition-all duration-1000 ${
          visibleSections.has('services-section')
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 lg:mb-14">
            <div className="flex items-center justify-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="h-px w-8 sm:w-12 bg-[#C9A84C]"></div>
              <span
                className="text-[#C9A84C] text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Our Services
              </span>
              <div className="h-px w-8 sm:w-12 bg-[#C9A84C]"></div>
            </div>
            <h2
              className="text-3xl sm:text-4xl font-bold text-white"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              What We Offer
            </h2>
          </div>

          {/* Mobile‑first grid: 1‑col → 2‑col → 4‑col */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-[#141414] border border-white/5 hover:border-[#C9A84C]/30 p-4 sm:p-5 rounded-sm transition-all duration-500 group cursor-pointer hover:shadow-xl hover:shadow-[#C9A84C]/5 animate-fadeInUp"
                style={{ animationDelay: `${index * 60}ms` }}
              >
                <div className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center border border-[#C9A84C]/30 group-hover:bg-[#C9A84C] group-hover:border-[#C9A84C] rounded-sm mb-4 sm:mb-5 transition-all duration-300">
                  <i
                    className={`${service.icon} text-lg sm:text-xl text-[#C9A84C] group-hover:text-[#0d0d0d] transition-colors duration-300`}
                  ></i>
                </div>
                <h3
                  className="text-base font-bold text-white mb-2"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {service.title}
                </h3>
                <p
                  className="text-white/70 text-sm leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
                >
                  {service.description}
                </p>
                <span
                  className="text-[#C9A84C] text-xs font-semibold tracking-wider"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {service.price}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --------------------------- Appointment Form --------------------------- */}
      <section
        id="form-section"
        ref={formRef}
        className={`py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-16 transition-all duration-1000 ${
          visibleSections.has('form-section')
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <div className="flex items-center justify-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="h-px w-8 sm:w-12 bg-[#C9A84C]"></div>
              <span
                className="text-[#C9A84C] text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Book Appointment
              </span>
              <div className="h-px w-8 sm:w-12 bg-[#C9A84C]"></div>
            </div>
            <h2
              className="text-3xl sm:text-4xl font-bold text-white"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Schedule Service
            </h2>
            <p
              className="text-white/40 text-xs sm:text-sm mt-2 sm:mt-3"
              style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
            >
              Book your appointment online and we'll take care of the rest.
            </p>
          </div>

          {/* Success message */}
          {submitSuccess ? (
            <div className="bg-[#141414] border border-[#C9A84C]/30 rounded-sm p-8 sm:p-10 lg:p-12 text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center border border-[#C9A84C]/40 rounded-sm mx-auto mb-5 sm:mb-6">
                <i className="ri-check-line text-2xl sm:text-3xl text-[#C9A84C]"></i>
              </div>
              <h3
                className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Appointment Requested!
              </h3>
              <p
                className="text-white/40 text-xs sm:text-sm mb-5 sm:mb-6"
                style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
              >
                Our team will contact you shortly to confirm your appointment.
              </p>
              <button
                onClick={() => setSubmitSuccess(false)}
                className="bg-[#C9A84C] text-[#0d0d0d] px-6 sm:px-8 py-2.5 sm:py-3 rounded-sm text-xs font-bold tracking-widest uppercase hover:bg-[#e0bc5e]"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Schedule Another
              </button>
            </div>
          ) : (
            // --------------------------- Form ---------------------------
            <form
              data-readdy-form
              id="service-appointment-form"
              onSubmit={handleSubmit}
              className="bg-[#141414] border border-white/5 rounded-sm p-4 sm:p-6 lg:p-8"
            >
              {/* Grid collapses to 1‑column on mobile */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-white/40 mb-2 tracking-wider uppercase">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className={inputClass}
                    placeholder="John Smith"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-white/40 mb-2 tracking-wider uppercase">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={inputClass}
                    placeholder="john@example.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-white/40 mb-2 tracking-wider uppercase">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className={inputClass}
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-white/40 mb-2 tracking-wider uppercase">
                    Service Type *
                  </label>
                  <div className="relative">
                    <select
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleInputChange}
                      required
                      className={`${inputClass} appearance-none pr-9 cursor-pointer`}
                    >
                      <option value="">Select a service</option>
                      <option value="Oil Change">Oil Change &amp; Fluids</option>
                      <option value="Brake Service">Brake Service</option>
                      <option value="Engine Diagnostics">
                        Engine Diagnostics
                      </option>
                      <option value="Detailing">Luxury Detailing</option>
                      <option value="Wheel Alignment">Wheel Alignment</option>
                      <option value="Battery Service">Battery Service</option>
                      <option value="A/C Service">A/C Service</option>
                      <option value="Full Inspection">Full Inspection</option>
                      <option value="Other">Other</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none"></i>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-white/40 mb-2 tracking-wider uppercase">
                    Vehicle Make *
                  </label>
                  <input
                    type="text"
                    name="vehicleMake"
                    value={formData.vehicleMake}
                    onChange={handleInputChange}
                    required
                    className={inputClass}
                    placeholder="Mercedes-Benz"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-white/40 mb-2 tracking-wider uppercase">
                    Vehicle Model *
                  </label>
                  <input
                    type="text"
                    name="vehicleModel"
                    value={formData.vehicleModel}
                    onChange={handleInputChange}
                    required
                    className={inputClass}
                    placeholder="S-Class"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-white/40 mb-2 tracking-wider uppercase">
                    Vehicle Year *
                  </label>
                  <input
                    type="text"
                    name="vehicleYear"
                    value={formData.vehicleYear}
                    onChange={handleInputChange}
                    required
                    className={inputClass}
                    placeholder="2024"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-white/40 mb-2 tracking-wider uppercase">
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    required
                    className={`${inputClass} cursor-pointer`}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-white/40 mb-2 tracking-wider uppercase">
                    Preferred Time *
                  </label>
                  <div className="relative">
                    <select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleInputChange}
                      required
                      className={`${inputClass} appearance-none pr-9 cursor-pointer`}
                    >
                      <option value="">Select a time</option>
                      {[
                        '8:00 AM',
                        '9:00 AM',
                        '10:00 AM',
                        '11:00 AM',
                        '12:00 PM',
                        '1:00 PM',
                        '2:00 PM',
                        '3:00 PM',
                        '4:00 PM',
                        '5:00 PM',
                      ].map(t => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none"></i>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-white/40 mb-2 tracking-wider uppercase">
                    Additional Details{' '}
                    <span className="text-white/20 normal-case">
                      ({formData.message.length}/500)
                    </span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    maxLength={500}
                    rows={4}
                    className={`${inputClass} resize-none`}
                    placeholder="Please describe any specific issues or concerns..."
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-6 sm:mt-8 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] py-3 sm:py-4 rounded-sm text-xs font-bold tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ri-calendar-check-line"></i>
                    Schedule Appointment
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* --------------------------- Location --------------------------- */}
      <section
        id="location-section"
        ref={locationRef}
        className={`py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-16 bg-[#111111] transition-all duration-1000 ${
          visibleSections.has('location-section')
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <div className="flex items-center justify-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="h-px w-8 sm:w-12 bg-[#C9A84C]"></div>
              <span
                className="text-[#C9A84C] text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Find Us
              </span>
              <div className="h-px w-8 sm:w-12 bg-[#C9A84C]"></div>
            </div>
            <h2
              className="text-3xl sm:text-4xl font-bold text-white"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Visit Our Service Center
            </h2>
          </div>

          {/* Stack vertically on mobile, side‑by‑side on lg */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            <div className="bg-[#141414] border border-white/5 rounded-sm overflow-hidden h-64 sm:h-80 lg:h-[400px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.9663095343008!2d-74.00425878428698!3d40.74076794379132!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c259bf5c1654f3%3A0xc80f9cfce5383d5d!2sGoogle!5e0!3m2!1sen!2sus!4v1635959481000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Service Center Location"
              />
            </div>

            <div className="bg-[#141414] border border-white/5 p-4 sm:p-6 lg:p-8 rounded-sm">
              <h3
                className="text-xl sm:text-2xl font-bold text-white mb-5 sm:mb-6"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Service Center Info
              </h3>
              <div className="space-y-5 sm:space-y-6">
                {[
                  {
                    icon: 'ri-map-pin-line',
                    title: 'Address',
                    detail: '1250 Luxury Auto Drive\nBeverly Hills, CA 90210',
                  },
                  {
                    icon: 'ri-time-line',
                    title: 'Service Hours',
                    detail:
                      'Mon – Fri: 7:30 AM – 6:00 PM\nSaturday: 8:00 AM – 4:00 PM\nSunday: Closed',
                  },
                  {
                    icon: 'ri-phone-line',
                    title: 'Contact',
                    detail:
                      'Phone: (310) 555-0199\nEmail: service@paramountmotors.com',
                  },
                  {
                    icon: 'ri-car-line',
                    title: 'Amenities',
                    detail:
                      'Complimentary loaner vehicles\nFree WiFi in waiting lounge\nShuttle service available',
                  },
                ].map(item => (
                  <div key={item.title} className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center border border-[#C9A84C]/30 rounded-sm flex-shrink-0">
                      <i className={`${item.icon} text-base sm:text-lg text-[#C9A84C]`}></i>
                    </div>
                    <div>
                      <h4
                        className="text-sm font-semibold text-white mb-1"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {item.title}
                      </h4>
                      <p
                        className="text-white/70 text-sm leading-relaxed"
                        style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
                      >
                        {item.detail}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------- Footer --------------------------- */}
      <footer className="bg-[#080808] border-t border-white/5 py-8 sm:py-10 lg:py-12 px-4 sm:px-8 lg:px-16">
        {/* Mobile‑first stacking */}
        <div className="flex flex-col items-center gap-6 md:flex-row md:justify-between md:items-center">
          <div className="flex items-center gap-2 sm:gap-3">
            <i className="ri-shield-star-fill text-[#C9A84C] text-lg sm:text-xl"></i>
            <span
              className="text-base sm:text-lg font-bold tracking-widest text-white uppercase"
              style={{
                fontFamily: 'Cormorant Garamond, serif',
                letterSpacing: '0.15em',
              }}
            >
              Paramount Motors
            </span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            {navItems.map(item => (
              <Link
                key={item.name}
                to={item.path}
                className="text-xs text-white/40 hover:text-[#C9A84C] transition-colors uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {item.name}
              </Link>
            ))}
          </div>

          <p className="text-xs text-white/20" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            © 2025 Paramount Motors
          </p>
        </div>
      </footer>

      {/* Simple keyframe animation */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeInUp { animation: fadeInUp 0.5s ease-out forwards; }
      `}</style>
    </div>
  );
}
